
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    inquiryType: 'procurement',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate form submission
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setFormData({
        name: '',
        organization: '',
        email: '',
        phone: '',
        inquiryType: 'procurement',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">Contact DFF Distribution</h1>
          <p className="text-xl text-slate-300 max-w-3xl">
            Initiate a partnership discussion with our team. We respond to all inquiries from qualified
            procurement professionals and supplier organizations.
          </p>
        </div>
      </section>

      {/* Contact Information Bar */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded-md mr-4 flex-shrink-0">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Email Address</h3>
                <a
                  href="mailto:CustomerSupport@dffdistribution.com"
                  className="text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
                >
                  CustomerSupport@dffdistribution.com
                </a>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded-md mr-4 flex-shrink-0">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Phone Number</h3>
                <a
                  href="tel:+16562627477"
                  className="text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
                >
                  (656) 262-7477
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Partnership Inquiries</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                DFF Distribution evaluates partnership opportunities based on alignment in values,
                operational standards, and long-term objectives. We prioritize relationships with
                organizations that share our commitment to compliance, reliability, and professional
                execution.
              </p>

              <div className="space-y-6 mb-12">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded-md mr-4 flex-shrink-0">
                    <i className="ri-building-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      For Procurement Professionals
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      If you represent a municipal, government, or commercial organization seeking
                      reliable wholesale distribution support, we invite you to describe your supply
                      chain needs and procurement requirements.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded-md mr-4 flex-shrink-0">
                    <i className="ri-store-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      For Manufacturers & Suppliers
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      If you're a manufacturer or authorized distributor seeking distribution channels
                      into institutional markets, we welcome discussions about potential partnership
                      alignment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded-md mr-4 flex-shrink-0">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      Response Timeline
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      We review all inquiries carefully and respond to qualified opportunities within
                      2-3 business days. Our evaluation process prioritizes alignment over speed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-8 rounded-lg border border-slate-200">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">What to Include</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <p className="text-slate-600">Your organization type and operational focus</p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <p className="text-slate-600">
                      Specific supply chain needs or distribution objectives
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <p className="text-slate-600">Relevant procurement or compliance requirements</p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <p className="text-slate-600">Timeline and partnership expectations</p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-slate-50 p-8 rounded-lg border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Send an Inquiry</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                      Full Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="organization"
                      className="block text-sm font-semibold text-slate-900 mb-2"
                    >
                      Organization <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                      Email Address <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="inquiryType"
                      className="block text-sm font-semibold text-slate-900 mb-2"
                    >
                      Inquiry Type <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent cursor-pointer"
                    >
                      <option value="procurement">Procurement / Buyer Inquiry</option>
                      <option value="supplier">Supplier / Manufacturer Partnership</option>
                      <option value="contract">Contract Supply Discussion</option>
                      <option value="general">General Partnership Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                      Message <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                      placeholder="Please describe your organization, supply chain needs, or partnership objectives..."
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-2">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="whitespace-nowrap w-full px-8 py-4 bg-slate-900 text-white font-semibold rounded-md hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm">
                      Thank you for your inquiry. We will review your message and respond within 2-3
                      business days.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                      There was an error submitting your inquiry. Please try again or contact us
                      directly.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
